import type { DashboardVideo, Project, User } from 'js/src/client';
import type { View } from 'js/src/data/dashboard/dashboard-types';
import { DEFAULT_VIDEOS_SORT_OPTION, VIDEOS_KEY } from 'js/src/data/dashboard/dashboard-types';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import type { DeepReadonly } from 'js/src/libs/consts';

/**
 * Returns a modified project that has properties necessary for video storage
 * Used when setting all projects for the first time or creating a new project
 * Else, use existing dashboard actions
 */
export function convertProjectToView(project: DeepReadonly<Project>): View {
    const videos = PlainJsStoreORM.createEmpty<DeepReadonly<DashboardVideo>, number>().data;
    return {
        id: project.id,
        name: project.name,
        subTitle: undefined,
        icon: project.icon,
        color: project.color,
        moreVideosToLoad: true,
        loadedUpToPage: 0,
        videosSortBy: DEFAULT_VIDEOS_SORT_OPTION,
        workspaceId: project.workspace,
        [VIDEOS_KEY]: videos,
    };
}

/**
 * - Parses an Immutable list of members from Redux to a format for react-select
 * - Omits the current user from the result
 */
export function parseMembers(
    memberList: readonly DeepReadonly<User>[],
    excludeCurrentUser: boolean = true,
): {
    label: string;
    value: number;
    valueLabel: string;
    primaryLabel: string;
}[] {
    if (!memberList || !memberList.length) {
        return [];
    }
    if (excludeCurrentUser) {
        memberList = memberList.filter((member) => member.id !== SERVER.USER.id);
    }
    return memberList
        .map((member) => {
            const { id, fullName, email, role } = getMemberDetails(member);
            return {
                label: fullName || email,
                value: id,
                valueLabel: fullName || email,
                primaryLabel: role as string,
            };
        })
        .sort((a, b) => {
            const labelA = a.label.toLowerCase();
            const labelB = b.label.toLowerCase();
            return labelA > labelB ? 1 : labelA < labelB ? -1 : 0;
        });
}

/**
 *
 * @param {*} member - Immutable map corresponding member object from backend.
 * @returns computed member properties as a plain JS object.
 */
export function getMemberDetails(member: DeepReadonly<User>) {
    const fullName = member.last_name
        ? `${member.first_name} ${member.last_name}`
        : member.first_name;
    const id = member.id;
    const email = member.email;
    const role = member.role;
    const photoUrl = member.photo_url;
    return {
        id,
        fullName,
        email,
        role,
        photoUrl,
    };
}
